import { compact, isEmpty, isNumber, toString, uniq } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LocationGroupScheduleComponentContextProps } from 'src/components/context/Context.interface';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants';
import { AssemblyJourneys, CodJourney } from 'src/constants/assemblyJourneys';
import { PATHS } from 'src/constants/paths';
import { useManagePositionLayout } from 'src/features/locations/scheduleComponentPage/hooks/useManagePositionLayout';
import { useManagePositionSidebar } from 'src/features/locations/scheduleComponentPage/hooks/useManagePositionSidebar';
import {
  useGetDefaultAssemblies,
  useGetLayoutScreens,
  useGetProjectConfig,
  useGetSchedule,
  useModal,
} from 'src/hooks';
import { useBulkCreateSchedule } from 'src/hooks/api/schedules/useBulkCreateSchedule';
import { useCreateChangeSet } from 'src/hooks/api/schedules/useCreateChangeSet';
import { useCreateSchedule } from 'src/hooks/api/schedules/useCreateSchedule';
import { useUpdateChangeSet } from 'src/hooks/api/schedules/useUpdateChangeSet';
import { useUpdateSchedule } from 'src/hooks/api/schedules/useUpdateSchedule';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { ILayout } from 'src/interfaces';
import {
  IBulkCreateSchedulePayload,
  ICreateScheduleItem,
  ICreateSchedulePayload,
  IUpdateScheduleItem,
  IUpdateSchedulePayload,
} from 'src/interfaces/Schedule';
import { GcfMockData } from 'src/utils/gcf-helper';

import { ILocationScheduleComponentState } from '../../scheduleLocationGroupPage/modules/scheduleComponentDrawer/ComponentScheduleDrawer.hook';
import { getMinPosition } from '../helpers/position.helper';
import { LocationGroupRow } from '../modules/additionalLocationGroups/AdditionalLocationGroupsTable';
import { getSelectedLocationGroupIds } from '../modules/reviewPageContent/helper';
import {
  buildChangeItemsFromScreensGridSections,
  buildChangeSetPayload,
  enrichChangesWithComponentData,
  filterOutInvalidDurationChanges,
  generateScheduleConfigPayload,
  getMinStartDate,
  getNewPositionsGridLines,
  getReviewPageProps,
  getScheduleComponentsPayload,
  hasScheduleChanged,
  initializeScreensGridSections,
} from '../ScheduleComponentPage.helper';
import {
  ChangeItem,
  ChangeOperation,
  ChangeSetPayload,
  ChangeSetUpdatePayload,
  IPositionGridLines,
  TScreensGridSections,
} from '../ScheduleComponentPage.interface';
import { usePublishableLocationGroups } from './usePublishableLocationGroups';
import { useValidateScreenStatus } from './useValidateScreenStatus';

export const useScheduleComponentPage = (scheduleId: string = '') => {
  const isEditingSchedule = !!scheduleId;

  // TODO: will remove if these states aren't used in the future
  const [customerState, setCustomerState] = useState<Partial<GcfMockData>>(
    GcfMockData.COD_CUSTOMER_UNKNOWN,
  );
  const [activeAssemblyJourney, setActiveAssemblyJourney] = useState<
    AssemblyJourneys | undefined
  >(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
  const [isCodConfirmationOpen, setIsCodConfirmationOpen] = useState<
    boolean | undefined
  >(undefined);

  const [activeStep, setActiveStep] = useState(1);
  const [layout, setLayout] = useState<ILayout | undefined>();
  const [positionsGridLines, setPositionsGridLines] = useState<
    IPositionGridLines[]
  >([]);
  const [screensGridSections, setScreensGridSections] =
    useState<TScreensGridSections>({});
  const [locationStateLocal, setLocationStateLocal] =
    useState<ILocationScheduleComponentState>();
  const [scheduleName, setScheduleName] = useState('');
  const [screensGridSectionChanges, setScreensGridSectionChanges] = useState<
    ChangeItem[]
  >([]);
  const { data: scheduleData, isLoading: isScheduleLoading } =
    useGetSchedule(scheduleId);
  const [selectedLocationGroups, setSelectedLocationGroups] = useState<
    Set<LocationGroupRow['id']>
  >(new Set());
  const [
    navigateAfterSaveWithoutPublication,
    setNavigateAfterSaveWithoutPublication,
  ] = useState(false);

  const createScheduleState = JSON.parse(
    localStorage.getItem('createScheduleState') || '{}',
  ) as ILocationScheduleComponentState;
  const createChangeSet = useCreateChangeSet();
  const updateChangeSet = useUpdateChangeSet();
  const [currentChangeSetId, setCurrentChangeSetId] = useState<number | null>(
    null,
  );
  const [lastPreviewSignature, setLastPreviewSignature] = useState<
    string | null
  >(null);
  const combinedState = {
    locationGroupId: isEditingSchedule
      ? toString(scheduleData?.location_group_id)
      : toString(locationStateLocal?.locationGroupId),
    daypartId: isEditingSchedule
      ? scheduleData?.daypart_id
      : locationStateLocal?.daypartId,
    additionalLocationGroups: isEditingSchedule
      ? false
      : locationStateLocal?.additionalLocationGroups,
    orientation: locationStateLocal?.orientation,
  };

  const { data: [defaultAssembly] = [], isLoading: isDefaultAssemblyLoading } =
    useGetDefaultAssemblies(
      [
        `location_group_id=${combinedState?.locationGroupId}`,
        `daypart_id=${combinedState?.daypartId}`,
        'valid_to.null=true',
      ],
      !!combinedState?.locationGroupId && !!combinedState?.daypartId,
      'timing',
    );

  const { data: projectConfig, isLoading: isProjectConfigLoading } =
    useGetProjectConfig();

  const { data: [initialLayout] = [], isLoading: isLayoutsLoading } =
    useGetLayoutScreens(
      defaultAssembly?.assembly?.layout_id,
      !!defaultAssembly?.assembly?.layout_id,
    );
  const isSavingRef = useRef(false);
  const formMethods = useForm();
  const confirmScheduleComponentsModal = useModal();
  const publicationModal = useModal();
  const navigate = useNavigate();
  const createSchedule = useCreateSchedule();
  const bulkCreateSchedule = useBulkCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (createScheduleState) {
      setLocationStateLocal(createScheduleState);
    }
    if (createScheduleState?.activeStep) {
      setActiveStep(createScheduleState.activeStep);
    }
    if (createScheduleState?.selectedLocationGroups) {
      setSelectedLocationGroups(
        new Set(createScheduleState.selectedLocationGroups),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    screenIndex,
    layerIndex,
    sectionIndex,
    previewingPositionIndex,
    showSectionDetailDrawer,
    setScreenIndex,
    setLayerIndex,
    setSectionIndex,
    setPreviewingPositionIndex,
    setShowSectionDetailDrawer,
    onUpdateScreensGridSectionsWhenSectionChanges,
    onUpdateScreensGridSectionsWhenAddingNewPosition,
  } = useManagePositionSidebar(layout, setScreensGridSections);

  const {
    showLayoutScreenBuilder,
    gridSectionsWithoutDisclaimer,
    currentSelectedSectionId,
    screenSections,
    layerScreenSections,
    setShowLayoutScreenBuilder,
    onActiveSectionChange,
    onGridSectionsChange,
    onDeleteScreenSection,
    onUpdateScreenPredefinedSections,
  } = useManagePositionLayout(
    screensGridSections,
    layerIndex,
    screenIndex,
    previewingPositionIndex,
    layout,
    positionsGridLines,
    setScreensGridSections,
    setShowSectionDetailDrawer,
  );

  const initialScheduleState = useRef<{
    positions: IPositionGridLines[];
    screensGridSections: TScreensGridSections;
    scheduleName: string;
  } | null>(null);

  if (
    !initialScheduleState.current &&
    positionsGridLines.length > 0 &&
    Object.keys(screensGridSections).length > 0 &&
    scheduleName.trim() !== ''
  ) {
    initialScheduleState.current = {
      positions: structuredClone(positionsGridLines),
      screensGridSections: structuredClone(screensGridSections),
      scheduleName: scheduleName.trim(),
    };
  }
  const hasUnsavedChanges = useMemo(() => {
    if (!initialScheduleState.current) return false;

    return hasScheduleChanged(initialScheduleState.current, {
      positions: positionsGridLines,
      screensGridSections,
      scheduleName,
    });
  }, [positionsGridLines, screensGridSections, scheduleName]);

  const hasLocalScheduleChanges = useMemo(() => {
    if (!initialScheduleState.current) return false;

    return hasScheduleChanged(initialScheduleState.current, {
      positions: positionsGridLines,
      screensGridSections,
    });
  }, [positionsGridLines, screensGridSections]);

  const {
    enabledSave,
    hadScheduledAllSections,
    hasAnyScheduledScreenForPosition,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    isPreviewEnabled,
  } = useValidateScreenStatus(
    layout as ILayout,
    screensGridSections,
    positionsGridLines,
    scheduleName,
    hasUnsavedChanges,
    hasLocalScheduleChanges,
    screensGridSectionChanges,
  );

  const reviewPageProps = getReviewPageProps(screensGridSections);
  const { publishableLocationGroupIds, hasLocationGroupsWithoutLocations } =
    usePublishableLocationGroups(
      selectedLocationGroups,
      combinedState?.locationGroupId,
    );

  const handlePostSaveSuccess = () => {
    confirmScheduleComponentsModal.onCloseModal();

    if (publishableLocationGroupIds.length > 0) {
      publicationModal.onOpenModal();
    } else {
      setNavigateAfterSaveWithoutPublication(true);
    }
  };

  const onUpdatePositionsGridLines = (
    newPositionGridLines: Partial<IPositionGridLines> & { position: number },
  ) => {
    setPositionsGridLines((prev) =>
      getNewPositionsGridLines(prev, newPositionGridLines),
    );
  };

  const handleCreateSchedule = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      const locationGroupIds = uniq(
        compact([...Array.from(selectedLocationGroups).map(Number)]),
      );

      // Get schedule items once to reuse for all location groups
      const scheduleItems =
        await getScheduleComponentsPayload<ICreateScheduleItem>(
          positionsGridLines,
          screensGridSections,
        );

      const defaultLocationPayload: ICreateSchedulePayload = {
        name: scheduleName,
        location_group_id: Number(combinedState?.locationGroupId),
        daypart_id: Number(combinedState?.daypartId),
        scheduleItems: scheduleItems,
        ...generateScheduleConfigPayload(layout, positionsGridLines),
        narrative: narrativeGenerators[COLLECTIONS.SCHEDULE_COMPONENTS][
          USER_ACTIONS.CREATE
        ](
          defaultAssembly?.locationGroup?.name ?? '',
          scheduleName,
          reviewPageProps.componentNames ?? [],
        ),
      };

      // If multiple location groups, use bulk creation
      if (locationGroupIds?.length > 0) {
        const bulkPayload: IBulkCreateSchedulePayload = {
          data: [
            {
              ...defaultLocationPayload,
              narrative: narrativeGenerators[
                COLLECTIONS.SCHEDULE_COMPONENTS_WITH_LGS
              ][USER_ACTIONS.CREATE](
                defaultAssembly?.locationGroup?.name ?? '',
                scheduleName,
              ),
            },
            ...locationGroupIds?.map((locationGroupId) => {
              const locationGroupName =
                createScheduleState?.locationGroupList?.find(
                  (locationGroup) => locationGroup.id === locationGroupId,
                )?.name ?? '';

              return {
                name: scheduleName,
                location_group_id: locationGroupId,
                daypart_id: Number(combinedState?.daypartId),
                ...generateScheduleConfigPayload(layout, positionsGridLines),
                scheduleItems: scheduleItems,
                narrative: narrativeGenerators[
                  COLLECTIONS.SCHEDULE_COMPONENTS_WITH_LGS
                ][USER_ACTIONS.CREATE](locationGroupName, scheduleName),
              };
            }),
          ],
        };

        const res = await bulkCreateSchedule.mutateAsync(bulkPayload);

        if (!isEmpty(res?.ids)) {
          handlePostSaveSuccess();
        }
      } else {
        // Single location group - use original API
        const res = await createSchedule.mutateAsync(defaultLocationPayload);

        if (res?.id) {
          handlePostSaveSuccess();
        }
      }

      // Remove createScheduleState
      localStorage.removeItem('createScheduleState');
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleUpdateSchedule = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      const payload: IUpdateSchedulePayload = {
        id: Number(scheduleId),
        name: scheduleName,
        ...generateScheduleConfigPayload(layout, positionsGridLines),
        scheduleItems: await getScheduleComponentsPayload<IUpdateScheduleItem>(
          positionsGridLines,
          screensGridSections,
        ),
        narrative: narrativeGenerators[
          COLLECTIONS.SCHEDULE_COMPONENTS_WITH_LGS
        ][USER_ACTIONS.UPDATE](
          defaultAssembly?.locationGroup?.name ?? '',
          scheduleName,
        ),
      };

      await updateSchedule.mutateAsync(payload);

      handlePostSaveSuccess();
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSaveDataDebounce = debounce(
    isEditingSchedule ? handleUpdateSchedule : handleCreateSchedule,
    1000,
    {
      leading: true,
      trailing: false,
    },
  );

  const handlePreviewSchedule = async () => {
    const locationGroupIds = [Number(combinedState.locationGroupId)];

    let changeSetId: number;
    let payload: ChangeSetPayload;

    if (!currentChangeSetId) {
      payload = buildChangeSetPayload({
        locationGroupIds,
        daypartId: combinedState.daypartId,
        scheduleItemChanges: filterOutInvalidDurationChanges(
          screensGridSectionChanges,
          screensGridSections,
        ),
      });
      const response = await createChangeSet.mutateAsync(payload);
      changeSetId = response.id;
      setCurrentChangeSetId(response.id);
    } else {
      payload = buildChangeSetPayload({
        locationGroupIds,
        scheduleItemChanges: filterOutInvalidDurationChanges(
          screensGridSectionChanges,
          screensGridSections,
        ),
      });
      await updateChangeSet.mutateAsync({
        changeSetId: currentChangeSetId,
        payload,
      });
      changeSetId = currentChangeSetId;
    }

    const enrichedChanges = enrichChangesWithComponentData(
      payload.changes,
      screensGridSections,
    );

    const earliestDate = getMinStartDate(enrichedChanges);

    const minPosition = getMinPosition(enrichedChanges, earliestDate);

    const previewPath = PATHS.SCHEDULE_PREVIEW(
      combinedState.locationGroupId,
      undefined,
      String(combinedState.daypartId),
      earliestDate ?? '',
      String(changeSetId),
      minPosition,
    );

    const previewUrl = `${window.location.origin}${window.location.pathname}#${previewPath}`;
    window.open(previewUrl, '_blank');
  };

  const handleCreatePreviewSchedule = async (locationGroupId?: number) => {
    const locationGroupIds = getSelectedLocationGroupIds(
      selectedLocationGroups,
      combinedState?.locationGroupId,
    );
    const daypartId = combinedState?.daypartId;

    if (!locationGroupIds.length || daypartId === undefined) return;

    const resolvedLocationGroupId =
      locationGroupId ?? locationGroupIds[0] ?? combinedState?.locationGroupId;

    if (resolvedLocationGroupId === undefined) return;

    const previewSignature = JSON.stringify({
      changes: screensGridSectionChanges,
    });

    let changeSetId: number;

    if (!currentChangeSetId) {
      const createPayload = buildChangeSetPayload({
        locationGroupIds,
        daypartId: Number(daypartId),
        scheduleItemChanges: screensGridSectionChanges,
      });
      const response = await createChangeSet.mutateAsync(createPayload);
      changeSetId = response.id;
      setCurrentChangeSetId(response.id);
      setLastPreviewSignature(previewSignature);
    } else if (lastPreviewSignature !== previewSignature) {
      const updatePayload: ChangeSetUpdatePayload = {
        changes: screensGridSectionChanges.map((change) => ({
          ...change,
          op: ChangeOperation.INSERT,
        })),
      };

      await updateChangeSet.mutateAsync({
        changeSetId: currentChangeSetId,
        payload: updatePayload,
      });

      changeSetId = currentChangeSetId;
      setLastPreviewSignature(previewSignature);
    } else {
      changeSetId = currentChangeSetId;
    }

    const enrichedChanges = enrichChangesWithComponentData(
      screensGridSectionChanges,
      screensGridSections,
    );

    const earliestDate = getMinStartDate(enrichedChanges);

    const minPosition = getMinPosition(enrichedChanges, earliestDate);

    const previewPath = PATHS.SCHEDULE_PREVIEW(
      String(resolvedLocationGroupId),
      undefined,
      String(combinedState.daypartId),
      earliestDate ?? '',
      String(changeSetId),
      minPosition,
    );

    const previewUrl = `${window.location.origin}${window.location.pathname}#${previewPath}`;
    window.open(previewUrl, '_blank');
  };

  const handleNext = (stepToIncrease?: number) => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

    const preCreateScheduleState = JSON.parse(
      localStorage.getItem('createScheduleState') || '{}',
    );
    // save the state on Schedule component step
    if (isNumber(stepToIncrease)) {
      localStorage.setItem(
        'createScheduleState',
        JSON.stringify({
          ...preCreateScheduleState,
          selectedLocationGroups: Array.from(selectedLocationGroups),
          activeStep: activeStep + stepToIncrease,
        } as ILocationScheduleComponentState),
      );
    }
    // reset url params
    setSearchParams({}, { replace: true });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setCustomerState(GcfMockData.COD_CUSTOMER_UNKNOWN);
    setActiveAssemblyJourney(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
    setIsCodConfirmationOpen(undefined);
    setShowLayoutScreenBuilder(false);
    setShowSectionDetailDrawer(false);
    setPreviewingPositionIndex(-1);
    setLayerIndex(-1);
  };

  const completeSteps = () => {
    confirmScheduleComponentsModal.onOpenModal(handleSaveDataDebounce);
  };

  const contextValues: LocationGroupScheduleComponentContextProps = {
    schedule: scheduleData,
    layout,
    daypartName: defaultAssembly?.daypart?.name ?? '',
    scheduleName: scheduleName,
    customerState,
    activeAssemblyJourney,
    isCodConfirmationOpen,
    screenIndex,
    showLayoutScreenBuilder,
    gridSectionsWithoutDisclaimer,
    currentSelectedSectionId,
    layerIndex,
    formMethods,
    sectionIndex,
    screenSections,
    showSectionDetailDrawer,
    layerScreenSections,
    previewingPositionIndex,
    positionsGridLines,
    daypartId: combinedState?.daypartId,
    selectedLocationGroups,
    selectedLocationGroupId: combinedState?.locationGroupId,
    orientation: combinedState?.orientation,
    isPreviewEnabled: isPreviewEnabled(),
    screensGridSections,
    isAdditionalLocationGroups:
      combinedState?.additionalLocationGroups ?? false,
    setCustomerState,
    setActiveAssemblyJourney,
    setIsCodConfirmationOpen,
    setScreenIndex,
    setShowLayoutScreenBuilder,
    onActiveSectionChange,
    onGridSectionsChange,
    setLayerIndex,
    onDeleteScreenSection,
    setLayout,
    setSectionIndex,
    onUpdateScreensGridSectionsWhenSectionChanges,
    setShowSectionDetailDrawer,
    onUpdateScreenPredefinedSections,
    hadScheduledAllSections,
    hasAnyScheduledScreenForPosition,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    setPreviewingPositionIndex,
    onUpdateScreensGridSectionsWhenAddingNewPosition,
    onUpdatePositionsGridLines,
    setScheduleName,
    setSelectedLocationGroups,
    handlePreviewSchedule,
    handleCreatePreviewSchedule,
  };

  useEffect(() => {
    if (initialLayout) {
      setLayout(initialLayout);
    }
  }, [initialLayout]);

  useEffect(() => {
    if (!screenSections[sectionIndex]) {
      setSectionIndex(screenSections.length - 1);
    }
  }, [screenSections, sectionIndex, setSectionIndex]);

  useEffect(() => {
    setScheduleName((prev) => {
      const sourceScheduleName = isEditingSchedule
        ? scheduleData?.name ?? ''
        : locationStateLocal?.scheduleName ?? '';

      if (prev === '') return sourceScheduleName;

      return prev; // preserve user edits
    });
  }, [locationStateLocal?.scheduleName, isEditingSchedule, scheduleData?.name]);

  useEffect(() => {
    if (initialLayout && projectConfig) {
      const { positions, gridLines } = initializeScreensGridSections(
        initialLayout,
        projectConfig,
        scheduleData,
      );

      setScreensGridSections(gridLines);
      setPositionsGridLines(positions);
    }
  }, [initialLayout, projectConfig, scheduleData]);

  const isContentLoading =
    isLayoutsLoading ||
    isDefaultAssemblyLoading ||
    isProjectConfigLoading ||
    (isEditingSchedule && isScheduleLoading);
  useEffect(() => {
    if (!initialScheduleState.current) return;

    const changes = buildChangeItemsFromScreensGridSections(
      initialScheduleState.current,
      { positions: positionsGridLines, screensGridSections },
    );

    setScreensGridSectionChanges(changes);
  }, [screensGridSections, positionsGridLines]);

  useEffect(() => {
    if (
      (isEditingSchedule && !scheduleData) ||
      (!isEditingSchedule && !locationStateLocal)
    )
      return;

    if (!combinedState?.locationGroupId) {
      navigate({ pathname: PATHS.LOCATIONS, search: '' }, { replace: true });
    }
  }, [
    combinedState?.locationGroupId,
    isEditingSchedule,
    locationStateLocal,
    navigate,
    scheduleData,
  ]);

  return {
    activeStep,
    contextValues,
    enabledSave,
    confirmScheduleComponentsModal,
    publicationModal,
    handleNext,
    handleBack,
    completeSteps,
    reviewPageProps,
    createSchedule,
    bulkCreateSchedule,
    updateSchedule,
    layout,
    isContentLoading,
    locationGroupId: combinedState?.locationGroupId,
    additionalLocationGroups: combinedState?.additionalLocationGroups,
    publishableLocationGroupIds,
    hasLocationGroupsWithoutLocations,
    navigateAfterSaveWithoutPublication,
    clearNavigateAfterSaveWithoutPublication: () =>
      setNavigateAfterSaveWithoutPublication(false),
    hasUnsavedChanges,
    screensGridSectionChanges,
    setScreensGridSectionChanges,
  };
};
